import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  ButtonGroup,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Tooltip,
  Autocomplete,
  Card,
  CardContent,
  TableContainer,
  TableCell,
  TableRow,
  Paper,
  TableHead,
  TableBody,
  Table,
} from "@mui/material";
import Tree from "react-d3-tree";
import TabTitle from "../TabTitle";
import CustomSelect from "../../theme/style-components/CustomSelect";
import AlignHorizontalRightIcon from "@mui/icons-material/AlignHorizontalRight";
import AlignVerticalBottomIcon from "@mui/icons-material/AlignVerticalBottom";
import CustomButton from "../../theme/style-components/CustomButton";
import Sharelink from "./Sharelink";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import {
  useGetChartDataQuery,
  useShareChartDataMutation,
} from "../../store/chart/ChartApiSlice";
import { useTheme } from "@emotion/react";
import { useGetDepartmentsListQuery } from "../../store/departments/DepartmentsApiSlice";
import { useGetTeamsListQuery } from "../../store/teams/TeamsApiSlice";
import NodeDetailModal from "./ChartNodeModal";
import { OrgChart } from "d3-org-chart";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import noProfile from '../../../src/assets/images/dashboard-image/noProfile.jpg'
import { selectCurrentUserObject } from "../../store/auth/authSlice";
import { useSelector } from "react-redux";
// Function to transform API data into tree structure
export const transformDataToTree = (data) => {
  console.log("🚀 ~ transformDataToTree ~ data123:", data)
  return data?.map((item) => ({
    department: item.department,
    description:item.description,
    eid:item.eid,
    location:item.location,
    parentId:item.parentId,
    name: item.name,
    id: item.id,
    imageUrl: item.imageUrl || "https://picsum.photos/100/100", // Fallback image
    email: item.email, // Add email
    phone: item.phone, // Add phone
    positionName:item.positionName,
    team:item.team,
    team_id: item.team_id,
  }));
  // return data.map((item) => ({
  //   name: item.name,
  //   // attributes: {
  //     id: item.id,
  //     imageUrl: item.image_url || "https://picsum.photos/100/100", // Fallback image
  //     title: item.title, // Add title
  //     email: item.email, // Add email
  //     phone: item.phone, // Add phone
  //     linked_url: item.linkedin_url,

  //   children:
  //     item.subordinates?.length > 0
  //       ? transformDataToTree(item.subordinates)
  //       : [],
  // }));
};

export const renderNodeWithImage = ({
  nodeDatum,
  toggleNode,
  setSelectedNode,
  setIsModalOpen,
}) => {
  const handleNodeClick = () => {
    setSelectedNode(nodeDatum); // Set the selected node
    setIsModalOpen(true); // Open the modal
  };

  return (
    <Tooltip title={nodeDatum?.name || "No details available"} arrow>
      <g onClick={handleNodeClick} style={{ cursor: "pointer" }}>
        <rect
          x={-170}
          y={100}
          width={364}
          height={95}
          fill={nodeDatum.fillColor || "#5bbba9"}
          stroke="#0070b661"
          strokeWidth={0}
          rx={20}
          ry={20}
        />
        <circle
          cx="0"
          cy="-60"
          r="60"
          fill="#fff"
          stroke="#0070b661"
          strokeWidth={3}
        />
        {nodeDatum.attributes?.imageUrl && (
          <image
            href={nodeDatum.attributes.imageUrl}
            x={-40}
            y={-120}
            width={80}
            height={120}
            rx={40}
            ry={40}
          />
        )}
        <text
          style={{
            textAnchor: "middle",
            fontSize: "33px",
          }}
          x={-5}
          y={70}
          fill="#fff"
          stroke="#5bbba9"
          strokeWidth="0"
          letterSpacing="3px"
        >
          {nodeDatum.name.length > 12
            ? nodeDatum.name.slice(0, 12).concat("...")
            : nodeDatum.name}
        </text>
      </g>
    </Tooltip>
  );
};

export default function OrganizationChart() {
  const [selectedDepartment, setSelectedDepartment] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [orientation, setOrientation] = useState("vertical");
  const [shareableLink, setShareableLink] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const user = useSelector(selectCurrentUserObject);
  // const containerRef = useRef();
  const containerRef = useRef();
  const theme = useTheme();
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

  const {
    isFetching: isChartLoading,
    data: chartReturned,
    error: isChartError,
  } = useGetChartDataQuery({
    team_ids: selectedTeams?.map((team) => team?.id),
    department_ids: selectedDepartment?.map((department) => department?.id),
    org_id: user?.company,
  },{ skip: !user?.company });
  
  const {
    isLoading: isDepartmentsLoading,
    data: DepartmentsData,
    error: isDepartmentsError,
  } = useGetDepartmentsListQuery({
    org_id: user?.company,
  },{ skip: !user?.company });

  const {
    isLoading: isTeamsLoading,
    data: TeamsData,
    error: isTeamsError,
  } = useGetTeamsListQuery({
    org_id: user?.company},{ skip: !user?.company });

  const filteredData = useMemo(() => {
    const employees = chartReturned?.employees || [];
    return employees.filter((employee) => {
      const matchesTeam =
        selectedTeams.length === 0 ||
        selectedTeams.some(team => employee.team_id.includes(team.id));
     const matchesDepartment =
        selectedDepartment.length === 0 ||
        selectedDepartment.some(dep => dep.name === employee.department);
      if (selectedTeams.length > 0 && selectedDepartment.length > 0) {
        return matchesTeam && matchesDepartment;
      }
  
      return matchesTeam || matchesDepartment;
    });
  }, [selectedTeams, selectedDepartment, chartReturned]);

  const renderTable = () => {
    return (
      <TableContainer style={{marginTop:"15px"}} component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{fontWeight:"700", fontSize:"15px",textAlign:"center"}}>Image</TableCell>
              <TableCell style={{fontWeight:"700", fontSize:"15px",textAlign:"center"}}>Name</TableCell>
              <TableCell style={{fontWeight:"700", fontSize:"15px",textAlign:"center"}}>Email</TableCell>
              <TableCell style={{fontWeight:"700", fontSize:"15px",textAlign:"center"}}>Phone</TableCell>
              <TableCell style={{fontWeight:"700", fontSize:"15px",textAlign:"center"}}>Team</TableCell>
              <TableCell style={{fontWeight:"700", fontSize:"15px" , textAlign:"center"}}>Position Name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length > 0 ? (
              filteredData.map((employee) => (
                <TableRow key={employee.id}>
                  <TableCell>
                    <img
                      src={employee?.imageUrl || noProfile}
                      style={{
                        height: "50px",
                        width: "50px",
                        borderRadius: "50%",
                      }}
                      alt="Profile"
                    />
                  </TableCell>
                  <TableCell style={{textAlign:"center"}}>{employee?.name || "N/A"}</TableCell>
                  <TableCell style={{textAlign:"center"}}>{employee?.email || "N/A"}</TableCell>
                  <TableCell style={{textAlign:"center"}}>{employee?.phone || "N/A"}</TableCell>
                  <TableCell style={{textAlign:"center"}}>{employee?.team || "N/A"}</TableCell>
                  <TableCell style={{textAlign:"center"}}>{employee?.positionName || "N/A"}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} style={{ textAlign: "center" }}>
                  No employees found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  const [shareChart] = useShareChartDataMutation();
  // const treeData = useMemo(() => chartReturned?.employees?.length ? structuredClone(chartReturned.employees) : [], [chartReturned?.employees]);
  // console.log("employee",treeData);
  const treeData = useMemo(() => {
    if (!chartReturned?.employees?.length) return [];
    return structuredClone(filteredData);
  }, [filteredData]);
  
  
  // Move the chart initialization useEffect to the top
  useEffect(() => {
    if (!containerRef.current || !treeData.length) return;

    const lastOccurrence = {};
    const updatedTreeData = treeData.map(item => {
      const updatedItem = { ...item };
      if (item.parentId && lastOccurrence.hasOwnProperty(item.parentId)) {
        updatedItem.parentIdOld = item.parentId;
        updatedItem.parentId = lastOccurrence[item.parentId];
      }
      lastOccurrence[item.eid.toString()] = item.id;
      return updatedItem;
    });

    const rootNode = treeData.find(item => !item.parentId);
    const rootNodeId = rootNode?.id ?? rootNode;

    // Clear previous content
    containerRef.current.innerHTML = '';
    const chart = new OrgChart()
      .container(containerRef.current)
      .data(updatedTreeData)
      .nodeWidth((d) => 240)
      .nodeHeight((d) => 150)
      .childrenMargin((d) => 110)
      .compactMarginBetween((d) => 45)
      .compactMarginPair((d) => 50)
      .neighbourMargin((a, b) => 25)
      .siblingsMargin((d) => 25)
      .nodeContent((d, i, arr, state) => {
        const isRoot = d.data.id === rootNode;
        return `
          <div style="font-family: Arial; background-color:none;height:${d.height}px;">
            <div style="background-color:${isRoot ? '#ffffff' : '#5bbba9'
          };margin-left:1px;height:${d.height - 2}px;border-radius:10px; border: ${isRoot ? '2px solid #5bbba9' : 'none'
          }">
        <div style="display:flex;justify-content:center;">
          <img src="${d.data?.image || d.data?.imageUrl
          }" style="margin-top:${isRoot ? '10' : '-30'
          }px;border-radius:${isRoot ? '0' : '100'
          }px;width:${isRoot ? '100' : '60'
          }px;height:${isRoot ? '50' : '60'
          }px;object-fit:contain; background-color: white; border:1px solid #5bbba9" />
              </div>
              <div style="color:${isRoot ? '#5bbba9' : '#ffffff'
          };font-size:16px;font-weight:bold;text-align:center;margin-top:${isRoot ? '5' : '35'
          }px;">
                ${d.data?.name || 'N/A'}
              </div>
              <div style="color:${isRoot ? '#5bbba9' : '#ffffff'
          };font-size:14px;text-align:center;margin-top:5px;">
                ${d.data.positionName || ''}
              </div>
            </div>
          </div>
        `;
      });
    chart.expandAll().fit();
    chart.render();
  }, [treeData]); // Add data as a dependency

  useEffect(() => {
    if (containerRef.current) {
      setDimensions({
        width: containerRef.current.offsetWidth,
        height: containerRef.current.offsetHeight,
      });
    }
  }, []);

  const initialZoom = 0.1;
  const initialTranslate = { x: dimensions.width / 1, y: 200 };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [isFullscreen]);

  const handleOrientationChange = (newOrientation) => {
    setOrientation(newOrientation);
  };

  const handleShareLink = () => {
    // const svgElement = containerRef?.current?.querySelector("svg");
    // if (svgElement) {
    //   const svgData = new XMLSerializer().serializeToString(svgElement);
    //   const encodedSvg = btoa(svgData);
    //   const svgUrl = `data:image/svg+xml;base64,${encodedSvg}`;
    //   setShareableLink(svgUrl);
      setOpenDialog(true);
    // }
  };

  const handleGeneratePDF = async () => {
    try {
      const chartElement = containerRef.current;
      if (!chartElement) return;

      // Create a canvas from the chart
      const canvas = await html2canvas(chartElement, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: chartElement.scrollWidth,
        height: chartElement.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc) => {
          // Reset any transforms on the SVG
          const svg = clonedDoc.querySelector('svg');
          if (svg) {
            svg.style.transform = 'none';
            svg.style.transformOrigin = '0 0';
          }
        }
      });

      // Create PDF in landscape mode
      const pdf = new jsPDF('l', 'mm', 'a4');

      // Calculate dimensions to fit the page
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Add the image to PDF
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

      // Save the PDF
      pdf.save('organization-chart.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard
      .writeText(shareableLink)
      .then(() => {
        setIsButtonDisabled(true);
        setTimeout(() => setIsButtonDisabled(false), 2000);
      })
      .catch((err) => {
        console.error("Error copying URL: ", err);
      });
  };

  const handleFullscreen = () => {
    const treeContainer = containerRef.current;
    if (!treeContainer) {
      console.warn("Tree container not available");
      return; // Avoid running the rest of the logic
    }
    if (!document.fullscreenElement) {
      treeContainer.style.backgroundColor = "#E2F1FE";
      if (treeContainer.requestFullscreen) {
        treeContainer.requestFullscreen();
      } else if (treeContainer.mozRequestFullScreen) {
        treeContainer.mozRequestFullScreen();
      } else if (treeContainer.webkitRequestFullscreen) {
        treeContainer.webkitRequestFullscreen();
      } else if (treeContainer.msRequestFullscreen) {
        treeContainer.msRequestFullscreen();
      }

      setIsFullscreen(true);
    } else {
      treeContainer.style.backgroundColor = "";
      setIsFullscreen(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Escape" && isFullscreen) {
      const treeContainer = containerRef.current;
      treeContainer.style.backgroundColor = "white";
      handleFullscreen();
    }
  };


  const renderContent = () => {
    if (isChartLoading || isDepartmentsLoading || isTeamsLoading) {
      return <Typography variant="body1">Loading...</Typography>;
    }

    if (isChartError || isDepartmentsError || isTeamsError) {
      return <Typography variant="body1">Error loading data.</Typography>;
    }

    if (selectedTeams.length > 0 || selectedDepartment.length > 0) {
      return renderTable();
    }

    return (
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          width: "100%",
          minHeight: "700px",
          backgroundColor: "transparent",
          overflow: "hidden",
          position: "relative",
          '& svg': {
            width: '100%',
            height: '100%',
          }
        }}
      />
    );
  };

  return (
    <Box>
      <TabTitle title="Organization chart" />
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              marginBottom: 2,
            }}
          >
            <Autocomplete
              multiple
              options={DepartmentsData?.data}
              getOptionLabel={(option) => option.name}
              onChange={(event, value) => setSelectedDepartment(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Departments"
                  placeholder="Departments"
                />
              )}
              sx={{
                "& .MuiFormControl-root ": {
                  "& .MuiInputLabel-root ": {
                    transform: "translate(14px, 12px) scale(1)",
                  },
                  "& .MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                  "& .MuiInputBase-root": {
                    "& .MuiInputBase-input": {
                      p: "4px 5px",
                      height: "20px",
                      lineHeight: "20px",
                    },
                    "& .MuiChip-root ": {
                      height: "22px",
                      lineHeight: "22px",
                      "& .MuiChip-label ": {
                        paddingLeft: "8px",
                        paddingRight: "8px",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "18px",
                        m: "0 2px 0 0",
                      },
                    },
                  },
                },
              }}
              style={{ width: "100%" }}
            />
            <Autocomplete
              multiple
              options={TeamsData?.data}
              getOptionLabel={(option) => option.name}
              onChange={(event, value) => setSelectedTeams(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Teams"
                  placeholder="Teams"
                />
              )}
              sx={{
                "& .MuiFormControl-root ": {
                  "& .MuiInputLabel-root ": {
                    transform: "translate(14px, 12px) scale(1)",
                  },
                  "& .MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                  "& .MuiInputBase-root": {
                    "& .MuiInputBase-input": {
                      p: "4px 5px",
                      height: "20px",
                      lineHeight: "20px",
                    },
                    "& .MuiChip-root ": {
                      height: "22px",
                      lineHeight: "22px",
                      "& .MuiChip-label ": {
                        paddingLeft: "8px",
                        paddingRight: "8px",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "18px",
                        m: "0 2px 0 0",
                      },
                    },
                  },
                },
              }}
              style={{ width: "100%" }}
            />
          </Box>
        </Grid>
      </Grid>

      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        style={{ gap: "10px", marginTop: "5px" }}
      >
        {selectedDepartment.length === 0 && selectedTeams.length === 0 && (
        <Button onClick={handleFullscreen}>
          <FullscreenIcon />
        </Button>
        )}
        {/* <ButtonGroup variant="outlined" aria-label="orientation selection">
          <Button
            onClick={() => handleOrientationChange("vertical")}
            style={{
              backgroundColor:
                orientation === "vertical" ? "#1F6EB9" : "transparent",
              color:
                orientation === "vertical"
                  ? "white"
                  : theme.palette.mode === "dark"
                    ? "white"
                    : "#000",
            }}
          >
            <AlignHorizontalRightIcon />
          </Button>
          <Button
            onClick={() => handleOrientationChange("horizontal")}
            style={{
              backgroundColor:
                orientation === "horizontal" ? "#1F6EB9" : "transparent",
              color:
                orientation === "horizontal"
                  ? "white"
                  : theme.palette.mode === "dark"
                    ? "white"
                    : "#000",
            }}
          >
            <AlignVerticalBottomIcon />
          </Button>
        </ButtonGroup> */}
        <Box>
          <Button
            onClick={handleShareLink}
            style={{
              backgroundColor: "#1F6EB9",
              color: "#fff",
              textTransform: "none",
            }}
          >
            Share
          </Button>
        </Box>
        <Box>
          <Button
            onClick={handleGeneratePDF}
            style={{
              backgroundColor: "#1F6EB9",
              color: "#fff",
              textTransform: "none",
            }}
          >
            PDF
          </Button>
        </Box>
      </Box>

      <div style={{ height: "100%", width: "100%", display: 'flex', flexDirection: 'column' }}>
        {renderContent()}
      </div>

      <Box>
        {openDialog && (
          <Sharelink
            openDialog={openDialog}
            shareableLink={shareableLink}
            handleCopyUrl={handleCopyUrl}
            setOpenDialog={setOpenDialog}
            isButtonDisabled={isButtonDisabled}
            shareChart={shareChart}
            department_ids={selectedDepartment.map((department) => department.id)}
            team_ids = {selectedTeams.map((teams)=>teams.id)}
          />
        )}
      </Box>

      <NodeDetailModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        node={selectedNode}
      />
    </Box>
  );
}